import React, { createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import { useAccount, useConnect, useChainId, useSwitchChain, useWalletClient } from 'wagmi';
import { base } from 'wagmi/chains';
import { getContract } from 'viem';
import toast from 'react-hot-toast';
import BaseBountyABI from '../utils/BaseBountyABI.json';

interface Web3ContextType {
  account: string | null;
  contract: any | null;
  isConnecting: boolean;
  isCorrectNetwork: boolean;
  connectWallet: () => void;
  switchToBase: () => void;
}

const Web3Context = createContext<Web3ContextType | undefined>(undefined);

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within Web3Provider');
  }
  return context;
};

interface Web3ProviderProps {
  children: ReactNode;
}

export const Web3Provider: React.FC<Web3ProviderProps> = ({ children }) => {
  const { address, isConnecting: wagmiConnecting } = useAccount();
  const { connect, connectors } = useConnect();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { data: walletClient } = useWalletClient();

  const isCorrectNetwork = chainId === base.id;
  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS as `0x${string}`;

  // Create contract instance with viem
  const contract = useMemo(() => {
    if (!walletClient || !contractAddress) return null;

    return getContract({
      address: contractAddress,
      abi: BaseBountyABI,
      client: walletClient,
    });
  }, [walletClient, contractAddress]);

  const connectWallet = () => {
    const injectedConnector = connectors.find(c => c.type === 'injected');
    if (injectedConnector) {
      connect({ connector: injectedConnector });
    } else {
      toast.error('Please install MetaMask or another Web3 wallet');
    }
  };

  const switchToBase = () => {
    switchChain({ chainId: base.id });
  };

  // Auto-switch to Base if connected to wrong network
  useEffect(() => {
    if (address && !isCorrectNetwork) {
      toast.error('Please switch to Base network');
    }
  }, [address, isCorrectNetwork]);

  const value = {
    account: address || null,
    contract,
    isConnecting: wagmiConnecting,
    isCorrectNetwork,
    connectWallet,
    switchToBase,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

